import { useCallback, useState } from "react";
import { GSI_REVERSE, fetchTochi } from "./data";
import { placeFromGsi, resolvePlace } from "./resolve";

// 玄関（/）の「宝蔵を開く（フリー）」を押したときに動く、いま立っている土地の判定。
//   位置（精度は粗くてよい）→ 国土地理院の逆ジオコーダで町字 → Functions の openTochi でその町字の頁を引く
//   （引いた頁は data.js が手元に持ち、TochiGlimpse がそのまま使う。1回押すと1回だけ呼ぶ）。
//   位置は保存しない。座標は国土地理院にだけ渡し、古道のサーバーには町字だけを送る。押すまで位置は聞かない。
//   結果はこのセッションの間だけ手元に持つ。戻ってきたときはそれを見せるが、扉を押したら必ず位置を取り直す
//   （開いたまま歩いて隣の町へ移ってから押すと、前の町の頁が出ていた。2026-10-27）。
//
// status: idle | locating | found（頁がある）| none（頁がない）| denied（位置を使えなかった）| error

const IDLE = { status: "idle", pos: null, place: null };
let cached = null;

export function getPosition(opts) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("no geolocation"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      reject,
      opts
    );
  });
}

/** 位置 → 町字 → 頁の有無。fetchImpl / open はテスト用 */
export async function checkCoverage(pos, fetchImpl = fetch, open = fetchTochi) {
  const r = await fetchImpl(`${GSI_REVERSE}?lat=${pos.lat}&lon=${pos.lng}`);
  const place = placeFromGsi(await r.json());
  if (!place) return { covered: false, place: null };
  const { data } = await open(place);
  const res = resolvePlace(data, place);
  // 村の頁があるか、江戸のころ海だった町字（埋立地）なら「頁がある」
  const covered = res.candidates.length > 0 || res.pending?.kind === "埋立地";
  return { covered, place };
}

export async function lookupHere() {
  let pos;
  try {
    // 端末に残っている古い位置は使わない（maximumAge 0）。精度は粗くてよい
    pos = await getPosition({ enableHighAccuracy: false, maximumAge: 0, timeout: 12000 });
  } catch (e) {
    // 位置を拒否・取れない。手元には残さない（設定を変えたらもう一度押せる）
    return { status: "denied", pos: null, place: null };
  }
  try {
    const { covered, place } = await checkCoverage(pos);
    cached = { status: covered ? "found" : "none", pos, place };
  } catch (e) {
    // 1日の上限など、理由が言えるものは言う
    return { status: "error", pos, place: null, message: e?.code === "functions/resource-exhausted" ? e.message : null };
  }
  return cached;
}

export default function useTochiLookup() {
  const [state, setState] = useState(cached || IDLE);
  const run = useCallback(async () => {
    setState((s) => ({ ...s, status: "locating" }));
    const r = await lookupHere();
    setState(r);
    return r;
  }, []);
  const reset = useCallback(() => setState(cached || IDLE), []);
  return { ...state, run, reset };
}

/** テスト用: セッション内の手元の結果を消す */
export function resetTochiLookup() {
  cached = null;
}
